// Training Programme (HRD-QR-01) — the AO's annual training plan, approved by Top Management before the
// training year begins (M&P 3.2 "Training Programme"; Appendix C). Replaces the Word form: one programme
// per year, each row a planned course with the months it is planned for.
// Admin / QM only (route-gated); writes are further restricted at the database by RLS.
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { format, parseISO } from 'date-fns'
import { toast, failed } from '../lib/toast'

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const CURRENT_YEAR = new Date().getFullYear()
const fmtD = d => { try { return d ? format(parseISO(d), 'd MMM yyyy') : '—' } catch (e) { return d || '—' } }

export default function TrainingProgramme() {
  const { profile } = useAuth()
  const [programmes, setProgrammes] = useState([])
  const [year, setYear] = useState(CURRENT_YEAR)
  const [prog, setProg] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(null)      // row being added/edited
  const blank = { department: '', designation: '', num_trainees: '', course_title: '', duration_text: '', months: [] }

  useEffect(() => { loadYears() }, [])              // eslint-disable-line
  useEffect(() => { loadYear(year) }, [year])       // eslint-disable-line

  async function loadYears() {
    const { data, error } = await supabase.from('training_programme').select('*').order('year', { ascending: false })
    if (error) { failed("Couldn't load the programme", error.message); setLoading(false); return }
    setProgrammes(data || [])
  }
  async function loadYear(y) {
    setLoading(true)
    const { data: p } = await supabase.from('training_programme').select('*').eq('year', y).maybeSingle()
    setProg(p || null)
    if (p) {
      const { data: r } = await supabase.from('training_programme_rows').select('*').eq('programme_id', p.id).order('sort_order')
      setRows(r || [])
    } else setRows([])
    setLoading(false)
  }

  async function createProgramme() {
    setBusy(true)
    const { data, error } = await supabase.from('training_programme')
      .insert({ year, status: 'Draft', prepared_by: profile?.name || null, prepared_on: new Date().toISOString().slice(0, 10) })
      .select().maybeSingle()
    setBusy(false)
    if (error) { failed("Couldn't create the programme", error.message); return }
    setProg(data); loadYears()
  }
  async function patchProgramme(patch) {
    const { error } = await supabase.from('training_programme').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', prog.id)
    if (error) { failed("Couldn't save", error.message); return }
    loadYear(year); loadYears()
  }
  async function approve() {
    if (!window.confirm('Mark this training programme as approved by Top Management?')) return
    patchProgramme({ status: 'Approved', approved_by: profile?.name || null, approved_on: new Date().toISOString().slice(0, 10) })
  }

  async function saveRow() {
    if (!(editing.course_title || '').trim()) { toast('Enter the course title', 'warning'); return }
    setBusy(true)
    const row = {
      programme_id: prog.id,
      department: editing.department || null,
      designation: editing.designation || null,
      num_trainees: editing.num_trainees === '' ? null : Number(editing.num_trainees),
      course_title: editing.course_title.trim(),
      duration_text: editing.duration_text || null,
      months: editing.months || [],
      sort_order: editing.id ? editing.sort_order : rows.length,
    }
    const res = editing.id
      ? await supabase.from('training_programme_rows').update(row).eq('id', editing.id)
      : await supabase.from('training_programme_rows').insert(row)
    setBusy(false)
    if (res.error) { failed("Couldn't save", res.error.message); return }
    setEditing(null); loadYear(year)
  }
  async function removeRow(r) {
    if (!window.confirm(`Remove "${r.course_title}" from the programme?`)) return
    await supabase.from('training_programme_rows').delete().eq('id', r.id)
    loadYear(year)
  }
  const toggleMonth = (m) => setEditing(e => {
    const cur = e.months || []
    return { ...e, months: cur.includes(m) ? cur.filter(x => x !== m) : [...cur, m].sort((a, b) => a - b) }
  })

  const yearOptions = Array.from(new Set([CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, ...programmes.map(p => p.year)])).sort((a, b) => b - a)

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Training Programme</h1>
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ width: 'auto' }}>
          {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {prog && <span className={`badge ${prog.status === 'Approved' ? 'badge-ok' : 'badge-gray'}`}>{prog.status}</span>}
      </div>

      <div className="card" style={{ marginBottom: 12, fontSize: 13, color: 'var(--text2)' }}>
        The annual training plan for the whole organisation (replaces form HRD-QR-01). Training needs are
        identified at least annually and whenever the management system, a person's responsibilities, a
        regulation or the team changes; the consolidated programme is approved by Top Management before the
        training year begins. Completed training is recorded against each person under Personnel → Training.
      </div>

      {loading ? <div className="card" style={{ color: 'var(--text3)' }}>Loading…</div> : !prog ? (
        <div className="card">
          <div style={{ fontSize: 13, marginBottom: 10 }}>No training programme exists for {year} yet.</div>
          <button className="primary" disabled={busy} onClick={createProgramme}>Create {year} programme</button>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="grid-2">
              <div className="field"><label>Prepared by</label>
                <input defaultValue={prog.prepared_by || ''} onBlur={e => patchProgramme({ prepared_by: e.target.value || null })} placeholder="Operations Manager" /></div>
              <div className="field"><label>Prepared on</label>
                <input type="date" defaultValue={prog.prepared_on || ''} onBlur={e => patchProgramme({ prepared_on: e.target.value || null })} /></div>
              <div className="field"><label>Approved by (Top Management)</label>
                <input defaultValue={prog.approved_by || ''} onBlur={e => patchProgramme({ approved_by: e.target.value || null })} /></div>
              <div className="field"><label>Approved on</label>
                <input type="date" defaultValue={prog.approved_on || ''} onBlur={e => patchProgramme({ approved_on: e.target.value || null })} /></div>
            </div>
            <div className="field"><label>Notes</label>
              <textarea rows={2} defaultValue={prog.notes || ''} onBlur={e => patchProgramme({ notes: e.target.value || null })} /></div>
            {prog.status !== 'Approved' && <button className="sm primary" onClick={approve}>Mark as approved by Top Management</button>}
            {prog.status === 'Approved' && <div style={{ fontSize: 13, color: 'var(--text2)' }}>Approved by {prog.approved_by || '—'} on {fmtD(prog.approved_on)}.</div>}
          </div>

          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
              <h2 className="card-title">Planned training ({rows.length})</h2>
              {!editing && <button className="sm primary" onClick={() => setEditing({ ...blank })}>+ Add course</button>}
            </div>

            {editing && (
              <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div className="grid-2">
                  <div className="field"><label>Department</label>
                    <input value={editing.department} onChange={e => setEditing({ ...editing, department: e.target.value })} placeholder="e.g. Audit / Operations / Sales" /></div>
                  <div className="field"><label>Designation</label>
                    <input value={editing.designation} onChange={e => setEditing({ ...editing, designation: e.target.value })} placeholder="e.g. Lead Auditor" /></div>
                  <div className="field"><label>No. of trainees</label>
                    <input type="number" value={editing.num_trainees} onChange={e => setEditing({ ...editing, num_trainees: e.target.value })} /></div>
                  <div className="field"><label>Duration</label>
                    <input value={editing.duration_text} onChange={e => setEditing({ ...editing, duration_text: e.target.value })} placeholder="e.g. 2 days" /></div>
                </div>
                <div className="field"><label>Course title *</label>
                  <input value={editing.course_title} onChange={e => setEditing({ ...editing, course_title: e.target.value })} /></div>
                <div className="field"><label>Planned month(s)</label>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {MONTH_NAMES.map((nm, i) => {
                      const m = i + 1, on = (editing.months || []).includes(m)
                      return (
                        <button key={m} type="button" className="sm" title={nm} onClick={() => toggleMonth(m)}
                          style={{ minWidth: 34, background: on ? 'var(--brand)' : 'var(--card)', color: on ? '#fff' : 'var(--text)', borderColor: on ? 'var(--brand)' : 'var(--border)' }}>
                          {MONTHS[i]}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="sm primary" disabled={busy} onClick={saveRow}>{busy ? 'Saving…' : 'Save course'}</button>
                  <button className="sm" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </div>
            )}

            {rows.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No courses planned yet.</div> : (
              <div className="table-wrap"><table>
                <thead><tr>
                  <th>Department</th><th>Designation</th><th style={{ width: 60 }}>Trainees</th><th>Course title</th><th style={{ width: 90 }}>Duration</th>
                  {MONTHS.map((m, i) => <th key={i} style={{ width: 20, textAlign: 'center', fontSize: 13 }} title={MONTH_NAMES[i]}>{m}</th>)}
                  <th style={{ width: 100 }}></th>
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontSize: 13 }}>{r.department || '—'}</td>
                      <td style={{ fontSize: 13 }}>{r.designation || '—'}</td>
                      <td style={{ fontSize: 13, textAlign: 'center' }}>{r.num_trainees ?? '—'}</td>
                      <td style={{ fontSize: 13, fontWeight: 500 }}>{r.course_title}</td>
                      <td style={{ fontSize: 13 }}>{r.duration_text || '—'}</td>
                      {MONTHS.map((_, i) => (
                        <td key={i} style={{ textAlign: 'center', fontSize: 13, color: 'var(--brand)', fontWeight: 700 }}>
                          {(r.months || []).includes(i + 1) ? '×' : ''}
                        </td>
                      ))}
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="sm" onClick={() => setEditing({ ...r, num_trainees: r.num_trainees ?? '', months: r.months || [] })}>Edit</button>{' '}
                        <button className="sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => removeRow(r)}>Del</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
